import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import moment from 'moment'
import RentalsAnalyticsMap from 'components/rentals/RentalsAnalyticsMap/RentalsAnalyticsMap'
import { MapLoader } from 'components/Map/MapLoader'
import RentalsAnalyticsDistribution from 'components/rentals/RentalsAnalyticsDistribution/RentalsAnalyticsDistribution'

import { selectHostsWithMostListings, selectListingsPerHost, selectHostsFilters } from 'store/rentals'

import { SECTION_HOST } from 'containers/Rentals/specs'
import styles from './RentalsAnalytics.scss'

interface DistributionData {
  key: string
  count: number
}

interface RentalsAnalyticsProps {
  isLoading?: boolean
  mode?: string
  clickOnGraph: (hostId: string) => void
  clearFiltersFromGraph: () => void
}

type DistributionKey = 'hostsWithMostListings' | 'listingsPerHost' | 'taxEvaders' | 'taxes'
type ClickFunctionKey = 'hostsWithMostListings' | 'listingsPerHost'
type ModeKey = 'hosts' | 'listings'

const listingsIndices = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10-15',
  '15-20',
  '20-30',
  '30-50',
  '50-100',
  '100-300',
  '300-1000',
  '1000-2500',
]

const taxEvaders: DistributionData[] = listingsIndices.map((listings, index) => ({
  key: listings,
  count: 5000 + 1000 * index,
}))

const allMonths = moment.monthsShort()

const taxes: DistributionData[] = allMonths.map((month, index) => ({
  key: month,
  count: 10000000 + 10000000 * index,
}))

const modes: Record<ModeKey, DistributionKey[]> = {
  hosts: ['hostsWithMostListings', 'listingsPerHost'],
  listings: ['listingsPerHost', 'taxes'],
}

const clearGraphFilter: Record<ClickFunctionKey, boolean> = {
  hostsWithMostListings: false,
  listingsPerHost: false,
}

const RentalsAnalytics: React.FC<RentalsAnalyticsProps> = ({
  isLoading = false,
  mode = SECTION_HOST,
  clickOnGraph,
  clearFiltersFromGraph,
}) => {
  const hostsWithMostListings = useSelector(selectHostsWithMostListings)
  const listingsPerHost = useSelector(selectListingsPerHost)
  const hostFilters = useSelector(selectHostsFilters)

  const distributionData: Record<DistributionKey, DistributionData[] | any> = {
    hostsWithMostListings,
    listingsPerHost: listingsPerHost.slice(0, 10),
    taxEvaders,
    taxes,
  }

  useEffect(() => {
    if (!!hostFilters.hostId) {
      clearGraphFilter.hostsWithMostListings = true
    }
  }, [hostFilters])

  const clickOnBar = (bar?: { indexValue: string }) => {
    if (!bar) return
    clearGraphFilter.hostsWithMostListings = true
    const { indexValue } = bar
    clickOnGraph(indexValue)
  }

  const clickOnMarker = (hostId: string) => {
    clearGraphFilter.hostsWithMostListings = true
    clickOnGraph(hostId)
  }

  const clickOnClear = () => {
    clearGraphFilter.hostsWithMostListings = false
    clearFiltersFromGraph()
  }

  const clickFunctions: Record<ClickFunctionKey, (bar?: { indexValue: string }) => void> = {
    hostsWithMostListings: clickOnBar,
    listingsPerHost: () => {},
  }

  const clearFunctions: Record<ClickFunctionKey, () => void> = {
    hostsWithMostListings: clickOnClear,
    listingsPerHost: () => {},
  }

  return (
    <div className={styles.container}>
      <ul className={styles.distributions}>
        {modes[mode as ModeKey].map(key => (
          <li key={key} className={styles.distribution}>
            <RentalsAnalyticsDistribution
              type={key}
              data={distributionData[key as DistributionKey]}
              className={styles.inner}
              clickOnGraph={clickFunctions[key as ClickFunctionKey]}
              graphClear={clearGraphFilter[key as ClickFunctionKey]}
              clearFiltersFromGraph={clearFunctions[key as ClickFunctionKey]}
            />
          </li>
        ))}
      </ul>
      <div className={styles.mapContainer}>
        {isLoading ? (
          <MapLoader />
        ) : (
          <RentalsAnalyticsMap listType='listings' clickOnMarker={clickOnMarker} clusterMode />
        )}
      </div>
    </div>
  )
}

RentalsAnalytics.propTypes = {
  isLoading: PropTypes.bool,
  mode: PropTypes.string,
}

export default RentalsAnalytics
